import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createNotice } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  ArrowLeft,
  Bell,
  AlertTriangle,
  Info,
  Calendar,
  FileText,
  Tag,
  Image,
  Upload,
  Save,
  Eye,
  AlertCircle,
  HelpCircle,
  Megaphone,
} from "lucide-react";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import { useLocationMembership } from "../../client/hooks/useLocationMembership";
import { AudienceSelect, type Audience } from "../../client/components/AudienceSelect";
import { MembersOnlyNotice } from "../../client/components/MembersOnlyNotice";

interface NoticeFormData {
  title: string;
  description: string;
  category: string;
  priority: "low" | "medium" | "high";
  tags: string;
  images: string[];
  isPinned: boolean;
  expiryDate: string;
}

const NOTICE_CATEGORIES = [
  { value: "announcement", label: "Announcement", icon: Bell },
  { value: "warning", label: "Warning", icon: AlertTriangle },
  { value: "info", label: "Information", icon: Info },
  { value: "maintenance", label: "Maintenance", icon: null },
  { value: "event", label: "Event", icon: Calendar },
  { value: "general", label: "General", icon: null },
];



// Tooltip component
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function AddNoticePage() {
  const { isMember, isLoading: membershipLoading, locationCode } = useLocationMembership();
  useRequireSectionVisible("showNotice");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [audience, setAudience] = useState<Audience>("VERIFIED");

  const [formData, setFormData] = useState<NoticeFormData>({
    title: "",
    description: "",
    category: "general",
    priority: "medium",
    tags: "",
    images: [],
    isPinned: false,
    expiryDate: "",
  });

  const handleChange = (field: keyof NoticeFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!selectedLocation?.id) {
      newErrors.location = "No location selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tagsArray = formData.tags
        ? formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean)
        : [];

      await createNotice({
        locationId: selectedLocation!.id,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        tags: tagsArray,
        images: formData.images,
        isPinned: formData.isPinned,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : null,
        visibility: audience,
        metadata: {
          priority: formData.priority,
        },
      });

      // Navigate back to notices
      handleGoBack();
    } catch (error) {
      console.error("Error creating notice:", error);
      setErrors({ submit: "Failed to create notice. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/notices`);
    } else {
      navigate("/notices");
    }
  };

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="notice-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Posting..." : "Post Notice"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <SidebarSection title="Information" hideOnMobile>
      <p className="text-sm text-gray-500">
        Post important updates, announcements, or alerts for your location.
      </p>
    </SidebarSection>
  );

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">
              You must be logged in to post notices.
            </p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!membershipLoading && !isMember) {
    return (
      <IndividualEditPageLayout title="Post a Notice" icon={Megaphone}>
        <MembersOnlyNotice locationCode={locationCode} />
      </IndividualEditPageLayout>
    );
  }

  return (
    <IndividualEditPageLayout
      title="Post a Notice"
      subtitle={selectedLocation?.name || "Select a location"}
      icon={Megaphone}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="notice-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="space-y-6">
          {/* What are you posting? */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-slate-900 ml-1">
              What are you posting?
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all ${
                errors.title ? "border-rose-500 bg-rose-50/50" : "border-slate-200"
              }`}
              placeholder="e.g., Scheduled Maintenance Notice, Community Event"
              required
            />
            {errors.title && (
              <p className="mt-1 text-sm text-rose-500 flex items-center gap-1 ml-1">
                <AlertCircle className="w-4 h-4" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-slate-900 ml-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={6}
              className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none ${
                errors.description ? "border-rose-500 bg-rose-50/50" : "border-slate-200"
              }`}
              placeholder="Provide detailed content of the notice, instructions, contact information..."
              required
            />
            {errors.description && (
              <p className="text-rose-500 text-sm mt-1 flex items-center gap-1 ml-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
          </div>
        </div>

        {/* Form Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700 ml-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => handleChange("category", e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
              >
                {NOTICE_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700 ml-1">Priority</label>
              <div className="flex p-1 bg-slate-100 rounded-xl">
                {["low", "medium", "high"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleChange("priority", p)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      formData.priority === p
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700 ml-1">Expiry Date (Optional)</label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => handleChange("expiryDate", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
              />
            </div>

            <div className="flex items-center gap-3 p-4 bg-blue-50/50 rounded-xl border border-blue-100/50">
              <input
                type="checkbox"
                id="isPinned"
                checked={formData.isPinned}
                onChange={(e) => handleChange("isPinned", e.target.checked)}
                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
              />
              <label htmlFor="isPinned" className="text-sm font-medium text-slate-700 cursor-pointer select-none">
                Pin this notice to the top
              </label>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-slate-700 ml-1">Tags</label>
          <div className="relative group">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => handleChange("tags", e.target.value)}
              placeholder="e.g. urgent, closure, parking (separate with commas)"
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
            />
          </div>
        </div>

        <AudienceSelect value={audience} onChange={setAudience} />

        {errors.submit && (
          <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">{errors.submit}</p>
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
